const DEFAULT_WHATSAPP = '5215515012488';
const DEFAULT_MIN_GUESTS = 100;

/**
 * Event type label sent to WhatsApp / cart attributes: the free text when "Otro" is chosen,
 * falling back to "Otro" itself when the free text is blank.
 * @param {string} eventType
 * @param {string} eventTypeOther
 * @param {string} otherValue
 */
export function resolveEventType(eventType, eventTypeOther, otherValue = 'Otro') {
  return eventType !== otherValue ? eventType : eventTypeOther.trim() || otherValue;
}

/**
 * @typedef {object} QuoteForm
 * @property {string} name
 * @property {string} phone
 * @property {string} email
 * @property {string} eventType
 * @property {string} eventTypeOther
 * @property {string} date
 * @property {string} guests
 * @property {string} location
 * @property {string[]} drinks
 * @property {string} budget
 * @property {string} comments
 */

/**
 * @param {QuoteForm} form
 * @param {string} [otherValue]
 */
export function buildQuoteMessage(form, otherValue = 'Otro') {
  return [
    '*Cotización de Evento*',
    '',
    '*Contacto*',
    `Nombre: ${form.name}`,
    `Teléfono: ${form.phone}`,
    `Email: ${form.email}`,
    '',
    '*Evento*',
    `Tipo: ${resolveEventType(form.eventType, form.eventTypeOther, otherValue)}`,
    `Fecha: ${form.date}`,
    `Personas: ${form.guests}`,
    `Ubicación: ${form.location}`,
    '',
    '*Preferencias*',
    form.drinks.length > 0 ? `Bebidas: ${form.drinks.join(', ')}` : '',
    form.budget ? `Presupuesto: ${form.budget}` : '',
    form.comments ? `Comentarios: ${form.comments}` : '',
  ]
    .filter(Boolean)
    .join('\n');
}

/**
 * @param {QuoteForm} form
 * @param {number} [minGuests]
 * @param {string} [otherValue]
 */
export function isQuoteValid(form, minGuests = DEFAULT_MIN_GUESTS, otherValue = 'Otro') {
  const hasEventType = !!form.eventType && (form.eventType !== otherValue || !!form.eventTypeOther.trim());
  return !!(
    form.name &&
    form.phone &&
    form.email &&
    hasEventType &&
    form.date &&
    form.location.trim() &&
    Number(form.guests) >= minGuests
  );
}

function digits(value) {
  return String(value || '').replace(/\D/g, '');
}

class MrbEventQuote extends HTMLElement {
  /** @type {string[]} */
  #drinks = [];

  connectedCallback() {
    this.form = /** @type {HTMLFormElement} */ (this.querySelector('[data-eq-form]'));
    this.formView = this.querySelector('[data-eq-form-view]');
    this.success = this.querySelector('[data-eq-success]');
    this.submitButton = this.querySelector('[data-eq-submit]');
    this.otherField = this.querySelector('[data-eq-other]');
    this.otherInput = this.otherField?.querySelector('input');
    if (!this.form) return;

    this.form.addEventListener('input', this.#onChange);
    this.form.addEventListener('change', this.#onChange);
    this.form.addEventListener('submit', this.#onSubmit);
    this.querySelector('[data-eq-reset]')?.addEventListener('click', this.#onReset);
    this.#sync();
  }

  disconnectedCallback() {
    this.form?.removeEventListener('input', this.#onChange);
    this.form?.removeEventListener('change', this.#onChange);
    this.form?.removeEventListener('submit', this.#onSubmit);
  }

  get otherValue() {
    return this.dataset.otherValue || 'Otro';
  }

  get minGuests() {
    const value = Number(this.dataset.minGuests);
    return Number.isFinite(value) && value > 0 ? value : DEFAULT_MIN_GUESTS;
  }

  get whatsappNumber() {
    const own = digits(this.dataset.whatsapp);
    if (own) return own;
    const floating = document.querySelector('a.mrb-whatsapp[href*="wa.me/"]');
    const match = floating?.getAttribute('href')?.match(/wa\.me\/(\d+)/);
    return match ? match[1] : DEFAULT_WHATSAPP;
  }

  /** @returns {QuoteForm} */
  get values() {
    const field = (name) => {
      const el = this.form.elements.namedItem(name);
      return el && 'value' in el ? String(el.value) : '';
    };
    return {
      name: field('name'),
      phone: field('phone'),
      email: field('email'),
      eventType: field('eventType'),
      eventTypeOther: this.otherInput ? this.otherInput.value : '',
      date: field('date'),
      guests: field('guests'),
      location: field('location'),
      drinks: [...this.#drinks],
      budget: field('budget'),
      comments: field('comments'),
    };
  }

  #onChange = (event) => {
    const target = event.target;
    if (event.type === 'change' && target instanceof HTMLInputElement && target.matches('[data-eq-drink]')) {
      const value = target.value;
      this.#drinks = target.checked
        ? [...this.#drinks.filter((drink) => drink !== value), value]
        : this.#drinks.filter((drink) => drink !== value);
    }
    this.#sync();
  };

  #sync() {
    const eventType = /** @type {HTMLSelectElement | null} */ (this.form.elements.namedItem('eventType'));
    const showOther = eventType?.value === this.otherValue;
    if (this.otherField && this.otherInput) {
      this.otherField.hidden = !showOther;
      this.otherInput.disabled = !showOther;
      this.otherInput.required = showOther;
    }
    if (this.submitButton) this.submitButton.disabled = !isQuoteValid(this.values, this.minGuests, this.otherValue);
  }

  #onSubmit = (event) => {
    event.preventDefault();
    const text = encodeURIComponent(buildQuoteMessage(this.values, this.otherValue));
    window.open(`https://wa.me/${this.whatsappNumber}?text=${text}`, '_blank');
    this.#showSuccess(true);
  };

  #onReset = () => {
    this.form.reset();
    this.#drinks = [];
    this.#sync();
    this.#showSuccess(false);
  };

  /** @param {boolean} visible */
  #showSuccess(visible) {
    if (this.formView) this.formView.hidden = visible;
    if (this.success) this.success.hidden = !visible;
    if (visible) {
      this.scrollIntoView({ block: 'start' });
      /** @type {HTMLElement | null} */ (this.querySelector('[data-eq-success-title]'))?.focus({ preventScroll: true });
    } else {
      this.scrollIntoView({ block: 'start' });
    }
  }
}

if (!customElements.get('mrb-event-quote')) customElements.define('mrb-event-quote', MrbEventQuote);
